
/**
 * Clase que representa la vista de la ventana de alertas.
 * @class
 */
class WarningsWindowView{

    /**
     * Elemento div html que contiene la ventana de alertas
     * @private
     */
    private readonly window: HTMLElement | null;

    /**
     * Función que notifica cuando se ha abierto o cerrado una ventana
     * @private
     */
    private notify: ((open: boolean) => any) | undefined | null = null;

    /**
     * Booleano que indica si está o no abierta la ventana de alertas
     * @private
     */
    private isopen: boolean = false;

    /**
     * Constructor de la clase WarningsWindowView.
     * @constructor
     * @param {HTMLElement} warningsWindowElement - Elemento HTML que representa la ventana de alertas.
     */
    constructor(warningsWindowElement: HTMLElement) {

        this.window = warningsWindowElement;


        const blur = document.getElementsByClassName("blur")[0];

        // Añade un listener para cerrar menús cuando se hace clic en cualquier parte de la pantalla (mobile).
        blur.addEventListener("click", () => {
            const event = new Event('closeMenus');
            document.dispatchEvent(event);
        });

        // Icono de cerrar menus
        const iconos_close_menus : any = this.window.getElementsByClassName("close");

        // Añade listeners para cerrar el menú al hacer clic en el icono de cierre.
        for (const icono_close of iconos_close_menus) {
            icono_close.addEventListener("click", this.close.bind(this) );
        }

        document.addEventListener("closeMenus", this.close.bind(this));
    }


    /**
     * Método que cierra la ventana de alertas.
     * @public
     */
    public close(){
        this.isopen = false;
        let blur = document.getElementsByClassName("blur")[0];
        blur.classList.remove("open");

        this.window?.classList.remove("open");

        if (this.notify)
            this.notify(false);

    }

    /**
     * Método que abre la ventana de alertas.
     * @public
     */
    public open(){
        this.isopen = true;
        this.window?.classList.add("open");

        // Se posiciona el scroll arriba
        const warnings:Element|undefined = this.window?.getElementsByClassName("tipos")[0];
        if (warnings)
            warnings.scrollTop=0;

        const blur = document.getElementsByClassName("blur")[0];

        blur?.classList.add("open");

        if (this.notify)
            this.notify(true);
    }

    /**
     * Asocia un manejador que notifica cuando se abre o cierra la ventana.
     * @param {(open: boolean) => any} handler - Función que maneja la notificación.
     * @public
     */
    bindNotify(handler: (open: boolean) => any){
        this.notify = handler;
    }

    /**
     * Devuelve un booleano indicando si está o no abierta la ventana
     */
    public isOpen(){
        return this.isopen;
    }


    /**
     * Actualiza la vista de la ventana de alertas con nuevos datos.
     *
     * @param {any} data - Datos de alertas.
     * @param {number} day - Día para el que se muestran las alertas.
     * @param {any} trans - Traducciones o textos necesarios.
     * @param {string} lang - Idioma utilizado.
     * @param {string} server - URL del servidor de alertas.
     * @param {boolean[]} filter - Filtros de alertas.
     * @public
     */
    public update(data: any, day: number, trans: any, lang: string, server: string, filter: boolean[]){

        // Elemento HTML donde se escribe el título
        const titulo = this.window?.getElementsByClassName("titulo")[0];
        // Elemento HTML donde se meten las alertas
        const tipos = this.window?.getElementsByClassName("tipos")[0];

        // Clases CSS
        const clases=["leve", "moderado", "importante", "extremo"];

        // Traducciones de los tipos de riesgo
        const tiposriesgo=[trans.RISK0, trans.RISK1, trans.RISK2, trans.RISK3]

        // Se cogen las alertas del día pasado por parámetro
        const daywarnings = data.respuesta.alertas[0].group.warnings.days.filter( function (e:any){ return e.day === day; });

        let warnings = [];

        if (daywarnings[0])
             warnings = daywarnings[0].warnings;

        // Provedores de las alertas
        const providers = data.respuesta.alertas[0].providers;

        // Contador del número de alertas que se muestran en la ventana
        let nalertas = 0;

        if (tipos) {

            // Se borran las alertas pintadas previamente en la ventana
            while (tipos.firstChild) {
                tipos.removeChild(tipos.firstChild);
            }

            // Se ordenan los warnings de mayor a menor riesgo de mayor a menor subriesgo
            warnings.sort(
                (a: any, b: any) =>{
                    if (a.risk > b.risk)
                        return -1;
                    if (a.risk < b.risk)
                        return 1;

                    if (a.subrisk > b.subrisk)
                        return -1;
                    if (a.subrisk < b.subrisk)
                        return 1;

                    return 0;
                }
            );

            // Se pintan cada una de las alertas
            for (const alerta of warnings) {
                if (filter[alerta.risk]) {
                    const li = document.createElement("li");

                    li.insertAdjacentHTML("afterbegin", `<div class='header'><div class='symbol ${clases[alerta.risk]}'><img src="${server}css/svgs/warnings/${alerta.type}.svg"/></div><div class='info'><span class='risk'>${tiposriesgo[alerta.risk]}</span><span class='type'>${alerta.phen}</span></div></div>`)
                    if (alerta.scope)
                        li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.GEOG_AREA}</span><span class="texto">${alerta.scope}</span></div>`)

                    if (alerta.start) {
                        const date = new Date(alerta.start);
                        const options: Intl.DateTimeFormatOptions = {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric"
                        };
                        const formattedDate = new Intl.DateTimeFormat(lang, options).format(date);

                        li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.WARNING_START}</span><span class="texto">${formattedDate}</span></div>`)
                    }

                    if (alerta.end) {
                        const date = new Date(alerta.end);
                        const options: Intl.DateTimeFormatOptions = {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric"
                        };
                        const formattedDate = new Intl.DateTimeFormat(lang, options).format(date);

                        li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.WARNING_END}</span><span class="texto">${formattedDate}</span></div>`)
                    }

                    if (alerta.probability)
                        li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.PROBABILITY}</span><span class="texto">${alerta.probability}</span></div>`)


                    if (alerta.description) {
                        let htmlimg = "";
                        let htmldescription = `<div class='field'><span class="titulo">${trans.DESCRIPTION}</span><span class="texto">${alerta.description.replace(/\n/g, "<br>")}</span> _IMAGES_ </div>`
                        // li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">Descripción</span><span class="texto">${alerta.description}</span>`)
                        if (alerta.images) {
                            for (const image of alerta.images)
                                htmlimg += `<img src="${image}"/>`;
                        }
                        htmldescription = htmldescription.replace("_IMAGES_", htmlimg);
                        li.insertAdjacentHTML("beforeend", htmldescription);
                    }

                    if (alerta.details)
                        li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.DETALLES}</span><span class="texto">${alerta.details.replace(/\n/g, "<br>")}</span></div>`)


                    if (alerta.provider) {
                        const provider = providers[alerta.provider];
                        if (provider)
                            li.insertAdjacentHTML("beforeend", `<div class='field'><span class="titulo">${trans.SOURCE}</span><span class="texto"><a href="${provider.url}">${provider.name}</a>. ${provider.description}</span></div>`)
                    }


                    tipos.appendChild(li);
                    nalertas++;
                }
            }
        }

        // Se pone cómo título el número de alertas y si son de hoy o mañana (si day es 0 o 1)
        if ( titulo ) {
            let texto="";
            if (day==0)
                texto=`${trans.TODAY}: `
            else if (day == 1)
                texto=`${trans.TOMORROW}: `

            if (nalertas == 0)
                texto += trans.ALERTAS_COUNT_ZERO.replace('_X_', nalertas);
            else if (nalertas == 1)
                texto += trans.ALERTAS_COUNT_ONE.replace('_X_', nalertas);
            else if (nalertas > 1 && nalertas <= 5)
                texto += trans.ALERTAS_COUNT_FEW.replace('_X_', nalertas);
            else
                texto += trans.ALERTAS_COUNT_OTHER.replace('_X_', nalertas);
            titulo.innerHTML = texto;
        }

    }
}

export default WarningsWindowView;
